import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Boxes, Search, Download, RefreshCw, Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import * as greigeStorageApi from '@/services/greigeStorageApi';

const LocationStockReport = () => {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState({ totalGroups: 0, totalBundles: 0, totalPieces: 0, uniqueBins: 0 });

  const [filters, setFilters] = useState({ location: '', design_name: '', barcode: '' });
  const [showFilters, setShowFilters] = useState(true);

  const fetchInventory = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (filters.location) params.location = filters.location;
      if (filters.design_name) params.design_name = filters.design_name;
      if (filters.barcode) params.barcode = filters.barcode;
      params.limit = 50000;

      const data = await greigeStorageApi.getLocationStockReport(params);
      if (data.success) {
        setInventory(data.inventory || []);
        setStats({
          totalGroups: data.total_groups || 0,
          totalBundles: data.total_bundles || 0,
          totalPieces: data.total_pieces || 0,
          uniqueBins: data.unique_bins || 0,
        });
      }
    } catch (error) {
      console.error('Error fetching location stock report:', error);
      toast.error('Failed to load location stock report');
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => fetchInventory();
  const handleClearFilters = () => setFilters({ location: '', design_name: '', barcode: '' });

  const csvCell = (v) => {
    const s = (v === null || v === undefined) ? '' : String(v);
    return `"${s.replace(/"/g, '""')}"`;
  };

  const handleExportCSV = () => {
    if (inventory.length === 0) {
      toast.error('No data to export');
      return;
    }
    const headers = ['Location', 'SKU Code', 'Design Name', 'Sort No', 'Colour', 'Border', 'Vendor', 'Bundle Number', 'Pieces'];
    const csvContent = [
      headers.join(','),
      ...inventory.map((item) => [
        csvCell(item.location_name),
        csvCell(item.sku_code),
        csvCell(item.design_name),
        csvCell(item.sort_no),
        csvCell(item.color_name),
        csvCell(item.border),
        csvCell(item.vendor_name),
        csvCell(item.bundle_barcode),
        item.pieces,
      ].join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `greige_location_stock_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Report exported successfully');
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="greige-location-stock-report">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/sections/greige-storage')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Boxes className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold font-display">Location Stock Report</h1>
                <p className="text-sm text-[#E8DCC8]">Stored greige bundles by location</p>
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={handleExportCSV}
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Bundles</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]" data-testid="stat-bundles">{stats.totalBundles.toLocaleString()}</p>
                </div>
                <Boxes className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Pieces</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]" data-testid="stat-pieces">{stats.totalPieces.toLocaleString()}</p>
                </div>
                <Boxes className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Locations Used</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]" data-testid="stat-bins">{stats.uniqueBins.toLocaleString()}</p>
                </div>
                <Boxes className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader className="py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filters
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn">
                  {showFilters ? 'Hide' : 'Show'} Filters
                </Button>
                <Button
                  onClick={handleSearch}
                  size="sm"
                  className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                  disabled={isLoading}
                  data-testid="search-btn"
                >
                  {isLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Search
                </Button>
              </div>
            </div>
          </CardHeader>
          {showFilters && (
            <CardContent className="pt-0 pb-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Location</label>
                  <Input
                    placeholder="Filter by location..."
                    value={filters.location}
                    onChange={(e) => setFilters((prev) => ({ ...prev, location: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    data-testid="filter-location"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Design Name</label>
                  <Input
                    placeholder="Filter by design..."
                    value={filters.design_name}
                    onChange={(e) => setFilters((prev) => ({ ...prev, design_name: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    data-testid="filter-design"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">SKU / Bundle Code</label>
                  <Input
                    placeholder="Filter by SKU or bundle..."
                    value={filters.barcode}
                    onChange={(e) => setFilters((prev) => ({ ...prev, barcode: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    data-testid="filter-barcode"
                  />
                </div>
                <div className="flex items-end">
                  <Button variant="outline" onClick={handleClearFilters} className="w-full" data-testid="clear-filters-btn">
                    <X className="w-4 h-4 mr-2" />
                    Clear
                  </Button>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Inventory Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">Location</TableHead>
                    <TableHead className="font-semibold">SKU Code</TableHead>
                    <TableHead className="font-semibold">Design Name</TableHead>
                    <TableHead className="font-semibold">Sort No</TableHead>
                    <TableHead className="font-semibold">Colour</TableHead>
                    <TableHead className="font-semibold">Border</TableHead>
                    <TableHead className="font-semibold">Vendor</TableHead>
                    <TableHead className="font-semibold">Bundle Number</TableHead>
                    <TableHead className="font-semibold text-right">Pieces</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : inventory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-gray-500" data-testid="empty-row">
                        No stored stock found. Try adjusting your filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    inventory.map((item, index) => (
                      <TableRow key={index} className="hover:bg-gray-50" data-testid={`stock-row-${index}`}>
                        <TableCell className="font-mono text-sm">{item.location_name}</TableCell>
                        <TableCell className="font-mono text-sm">{item.sku_code || '-'}</TableCell>
                        <TableCell>{item.design_name || '-'}</TableCell>
                        <TableCell>{item.sort_no || '-'}</TableCell>
                        <TableCell>{item.color_name || '-'}</TableCell>
                        <TableCell data-testid={`stock-border-${index}`}>{item.border || '-'}</TableCell>
                        <TableCell>{item.vendor_name || '-'}</TableCell>
                        <TableCell className="font-mono text-sm" data-testid={`stock-bundle-${index}`}>{item.bundle_barcode || '-'}</TableCell>
                        <TableCell className="text-right font-semibold text-green-600">{item.pieces}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {inventory.length > 0 && (
              <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Showing {inventory.length.toLocaleString()} bundles (STORED only)
                </span>
                <Badge variant="outline" className="text-lg px-4 py-1">
                  Total Pieces: {stats.totalPieces.toLocaleString()}
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default LocationStockReport;
